import { rtkApi, GET_ACADEMIC_STAFF } from '@/shared/api/rtkApi';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import {
  AcademicStaffResponse,
  AcademicStaffAllResponse,
  AcademicStaffParams,
  DeleteAcademicStaffResponse,
  AcademicStaffDetailResponse,
  CreateAcademicStaffRequest,
  CreateAcademicStaffResponse,
  UpdateAcademicStaffRequest,
  UpdateAcademicStaffResponse,
  BulkDeleteAcademicStaffRequest,
  BulkDeleteAcademicStaffResponse,
} from './types';

// Personal details stored in the staff profile, sent the same way on create and update
const STAFF_PERSONAL_DETAIL_FIELDS = [
  'gender',
  'genderOther',
  'addressLine2',
  'postcode',
  'country',
] as const satisfies readonly (keyof CreateAcademicStaffRequest &
  keyof UpdateAcademicStaffRequest)[];

const buildAcademicStaffUrl = (params: AcademicStaffParams) => {
  const searchParams = new URLSearchParams();

  if (params.page !== undefined) searchParams.append('page', String(params.page));
  if (params.pageSize !== undefined) searchParams.append('pageSize', String(params.pageSize));
  if (params.search) searchParams.append('search', params.search);
  if (params.jobTitle) searchParams.append('jobTitle', params.jobTitle);
  if (params.lineManager !== undefined) searchParams.append('lineManager', String(params.lineManager));
  if (params.courseId !== undefined) searchParams.append('courseId', String(params.courseId));
  if (params.sort) searchParams.append('sort', params.sort);

  const departments = Array.isArray(params.department)
    ? params.department
    : params.department !== undefined
      ? [params.department]
      : [];
  departments.forEach((departmentId) => {
    searchParams.append('department', String(departmentId));
  });

  const query = searchParams.toString();
  return query ? `/admin/academic-staff?${query}` : '/admin/academic-staff';
};

export const academicStaffApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getAcademicStaffAll: build.query<AcademicStaffAllResponse, void>({
      query: () => ({
        url: '/admin/academic-staff/all',
      }),
      providesTags: [GET_ACADEMIC_STAFF],
    }),
    getAcademicStaff: build.query<AcademicStaffResponse, AcademicStaffParams>({
      query: (params) => buildAcademicStaffUrl(params),
      providesTags: [GET_ACADEMIC_STAFF],
    }),
    getAcademicStaffById: build.query<AcademicStaffDetailResponse, number>({
      query: (id) => ({
        url: `/admin/academic-staff/${id}`,
      }),
      providesTags: [GET_ACADEMIC_STAFF],
    }),
    createAcademicStaff: build.mutation<CreateAcademicStaffResponse, CreateAcademicStaffRequest>({
      queryFn: async (body) => {
        const formData = new FormData();
        formData.append('firstName', body.firstName);
        formData.append('lastName', body.lastName);
        formData.append('phone', body.phone);
        formData.append('email', body.email);
        formData.append('city', body.city);
        formData.append('address', body.address);
        STAFF_PERSONAL_DETAIL_FIELDS.forEach((field) => {
          const value = body[field];
          if (value) formData.append(field, value);
        });
        if (body.password?.trim()) {
          formData.append('password', body.password.trim());
        }
        formData.append('about', body.about);
        formData.append('roleId', String(body.roleId));

        if (body.avatar) {
          formData.append('avatar', body.avatar);
        }

        if (body.jobTitle) {
          formData.append('jobTitle', body.jobTitle);
        }

        if (body.businessEmail) {
          formData.append('businessEmail', body.businessEmail);
        }

        if (body.emergencyFirstName) formData.append('emergencyFirstName', body.emergencyFirstName);
        if (body.emergencyLastName) formData.append('emergencyLastName', body.emergencyLastName);
        if (body.emergencyPhone) formData.append('emergencyPhone', body.emergencyPhone);
        if (body.emergencyRelationship)
          formData.append('emergencyRelationship', body.emergencyRelationship);
        if (body.emergencyRelationshipOther)
          formData.append('emergencyRelationshipOther', body.emergencyRelationshipOther);

        if (body.cvFile) {
          formData.append('cvFile', body.cvFile);
        }

        if (body.personalDocuments && body.personalDocuments.length > 0) {
          body.personalDocuments.forEach((file) => {
            formData.append('personalDocuments', file);
          });
        }

        if (body.diplomas && body.diplomas.length > 0) {
          body.diplomas.forEach((file) => {
            formData.append('diplomas', file);
          });
        }

        if (body.annualLeaveEntitlementDays !== undefined && body.annualLeaveEntitlementDays !== null) {
          formData.append('annualLeaveEntitlementDays', String(body.annualLeaveEntitlementDays));
        }

        if (body.socialLinks && body.socialLinks.length > 0) {
          formData.append('socialLinks', JSON.stringify(body.socialLinks));
        }

        if (body.assignedCourses !== undefined && body.assignedCourses !== null) {
          if (typeof body.assignedCourses === 'number') {
            formData.append('assignedCourses', String(body.assignedCourses));
          } else if (Array.isArray(body.assignedCourses)) {
            formData.append('assignedCourses', JSON.stringify(body.assignedCourses));
          } else {
            formData.append('assignedCourses', body.assignedCourses);
          }
        }

        const accessToken = LocalStorageService.getAccessToken();
        const headers = new Headers();
        if (accessToken) {
          headers.append('Authorization', `Bearer ${accessToken}`);
        }
        headers.append('Accept', 'application/json');

        try {
          const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiUrl}/admin/academic-staff`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: formData,
          });

          const data = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: data,
              },
            };
          }

          return { data };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: [GET_ACADEMIC_STAFF],
    }),
    updateAcademicStaff: build.mutation<
      UpdateAcademicStaffResponse,
      { id: number; body: UpdateAcademicStaffRequest }
    >({
      queryFn: async ({ id, body }) => {
        const formData = new FormData();

        if (body.firstName !== undefined) {
          formData.append('firstName', body.firstName);
        }
        if (body.lastName !== undefined) {
          formData.append('lastName', body.lastName);
        }
        if (body.phone !== undefined) {
          formData.append('phone', body.phone);
        }
        if (body.email !== undefined) {
          formData.append('email', body.email);
        }
        if (body.city !== undefined) {
          formData.append('city', body.city);
        }
        if (body.address !== undefined) {
          formData.append('address', body.address);
        }
        // Empty string clears the stored value
        STAFF_PERSONAL_DETAIL_FIELDS.forEach((field) => {
          const value = body[field];
          if (value !== undefined) formData.append(field, value ?? '');
        });
        if (body.password?.trim()) {
          formData.append('password', body.password.trim());
        }
        if (body.about !== undefined) {
          formData.append('about', body.about);
        }
        if (body.roleId !== undefined) {
          formData.append('roleId', String(body.roleId));
        }

        if (body.avatar !== undefined) {
          if (body.avatar) {
            formData.append('avatar', body.avatar);
          }
        }

        if (body.jobTitle !== undefined) {
          if (body.jobTitle) {
            formData.append('jobTitle', body.jobTitle);
          }
        }

        if (body.businessEmail !== undefined) {
          if (body.businessEmail) {
            formData.append('businessEmail', body.businessEmail);
          }
        }

        if (body.emergencyFirstName !== undefined)
          formData.append('emergencyFirstName', body.emergencyFirstName ?? '');
        if (body.emergencyLastName !== undefined)
          formData.append('emergencyLastName', body.emergencyLastName ?? '');
        if (body.emergencyPhone !== undefined)
          formData.append('emergencyPhone', body.emergencyPhone ?? '');
        if (body.emergencyRelationship !== undefined)
          formData.append('emergencyRelationship', body.emergencyRelationship ?? '');
        if (body.emergencyRelationshipOther !== undefined)
          formData.append('emergencyRelationshipOther', body.emergencyRelationshipOther ?? '');

        if (body.cvFile !== undefined) {
          if (body.cvFile) {
            formData.append('cvFile', body.cvFile);
          }
        }

        if (body.personalDocuments !== undefined && body.personalDocuments && body.personalDocuments.length > 0) {
          body.personalDocuments.forEach((file) => {
            formData.append('personalDocuments', file);
          });
        }

        if (body.removePersonalDocumentKeys?.length) {
          formData.append('removePersonalDocumentKeys', JSON.stringify(body.removePersonalDocumentKeys));
        }

        if (body.diplomas !== undefined && body.diplomas && body.diplomas.length > 0) {
          body.diplomas.forEach((file) => {
            formData.append('diplomas', file);
          });
        }

        if (body.removeDiplomaKeys?.length) {
          formData.append('removeDiplomaKeys', JSON.stringify(body.removeDiplomaKeys));
        }

        if (body.annualLeaveEntitlementDays !== undefined && body.annualLeaveEntitlementDays !== null) {
          formData.append('annualLeaveEntitlementDays', String(body.annualLeaveEntitlementDays));
        }

        if (body.socialLinks !== undefined) {
          if (body.socialLinks && body.socialLinks.length > 0) {
            formData.append('socialLinks', JSON.stringify(body.socialLinks));
          }
        }

        if (body.assignedCourses !== undefined) {
          if (typeof body.assignedCourses === 'number') {
            formData.append('assignedCourses', String(body.assignedCourses));
          } else if (Array.isArray(body.assignedCourses)) {
            formData.append('assignedCourses', JSON.stringify(body.assignedCourses));
          } else if (body.assignedCourses !== null) {
            formData.append('assignedCourses', body.assignedCourses);
          }
        }

        const accessToken = LocalStorageService.getAccessToken();
        const headers = new Headers();
        if (accessToken) {
          headers.append('Authorization', `Bearer ${accessToken}`);
        }
        headers.append('Accept', 'application/json');

        try {
          const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiUrl}/admin/academic-staff/${id}`, {
            method: 'PUT',
            headers,
            credentials: 'include',
            body: formData,
          });

          const data = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: data,
              },
            };
          }

          return { data };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: [GET_ACADEMIC_STAFF],
    }),
    deleteAcademicStaff: build.mutation<DeleteAcademicStaffResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/admin/academic-staff/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [GET_ACADEMIC_STAFF],
    }),
    bulkDeleteAcademicStaff: build.mutation<BulkDeleteAcademicStaffResponse, BulkDeleteAcademicStaffRequest>({
      query: (body) => ({
        url: '/admin/academic-staff/bulk-delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_ACADEMIC_STAFF],
    }),
  }),
});

export const {
  useGetAcademicStaffAllQuery,
  useGetAcademicStaffQuery,
  useGetAcademicStaffByIdQuery,
  useCreateAcademicStaffMutation,
  useUpdateAcademicStaffMutation,
  useDeleteAcademicStaffMutation,
  useBulkDeleteAcademicStaffMutation,
} = academicStaffApi;
